import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icons';
import LanguageSwitcher from './LanguageSwitcher';
import './AppShell.css';

// Menu, grouped. `end` keeps "/" from looking active on every tenant page.
const ADMIN_NAV = [
  { group: 'overview', items: [{ to: '/admin/dashboard', key: 'nav.dashboard', icon: 'dashboard' }] },
  {
    group: 'billing',
    items: [
      { to: '/admin/add-bill', key: 'nav.addBill', icon: 'plus' },
      { to: '/admin/bulk-bills', key: 'nav.bulkBills', icon: 'upload' },
      { to: '/admin/view-bills', key: 'nav.viewBills', icon: 'receipt' },
      { to: '/admin/paid-report', key: 'nav.paidReport', icon: 'chart' },
      { to: '/admin/notes', key: 'nav.roomNotes', icon: 'list' },
      { to: '/admin/expenses', key: 'nav.expenses', icon: 'rupee' },
    ],
  },
  {
    group: 'tenants',
    items: [
      { to: '/admin/users', key: 'nav.manageUsers', icon: 'users' },
      { to: '/admin/tenant-docs', key: 'nav.tenantDocs', icon: 'folder' },
      { to: '/admin/complaints', key: 'nav.complaints', icon: 'wrench' },
      { to: '/admin/vacate', key: 'nav.vacateRequests', icon: 'door' },
      { to: '/admin/archived', key: 'nav.archivedTenants', icon: 'archive' },
    ],
  },
];

const TENANT_NAV = [
  {
    group: null,
    items: [
      { to: '/', key: 'nav.myBills', icon: 'home', end: true },
      { to: '/history', key: 'nav.history', icon: 'history' },
      { to: '/complaints', key: 'nav.complaints', icon: 'wrench' },
      { to: '/occupants', key: 'nav.occupants', icon: 'family' },
      { to: '/vacate', key: 'nav.vacate', icon: 'door' },
    ],
  },
];

const initials = (name) =>
  String(name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

export default function AppShell({ user, onLogout, footer, children }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isAdmin = user.role === 'ADMIN';
  const nav = isAdmin ? ADMIN_NAV : TENANT_NAV;

  // Close the phone drawer once a page is picked.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Pages show their own heading, so the bar names the menu section instead of repeating it.
  const section = nav.find((g) => g.items.some((i) =>
    i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)));
  const displayName = user.fullName || user.username;

  return (
    <div className={`shell ${isAdmin ? 'shell--admin' : 'shell--tenant'}`}>
      <aside className={`sidebar ${drawerOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar__brand">
          <div className="sidebar__logo"><img src="/vgr-logo.png" alt="" /></div>
          <div>
            <div className="sidebar__name">VGR</div>
            <div className="sidebar__tag">{t('header.title')}</div>
          </div>
          <button type="button" className="sidebar__close" aria-label="Close menu" onClick={() => setDrawerOpen(false)}>
            <Icon name="close" />
          </button>
        </div>

        <nav className="sidebar__nav" aria-label="Main">
          {nav.map((g) => (
            <div className="sidebar__group" key={g.group || 'main'}>
              {g.group && <div className="sidebar__group-title">{t(`navGroup.${g.group}`)}</div>}
              {g.items.map((i) => (
                <NavLink key={i.to} to={i.to} end={i.end} className={({ isActive }) => `navitem ${isActive ? 'navitem--active' : ''}`}>
                  <Icon name={i.icon} className="navitem__icon" />
                  <span className="navitem__label">{t(i.key)}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

      </aside>
      {drawerOpen && <div className="scrim" onClick={() => setDrawerOpen(false)} aria-hidden="true" />}

      <div className="main">
        <header className="topbar">
          <button type="button" className="topbar__menu" aria-label="Open menu" onClick={() => setDrawerOpen(true)}>
            <Icon name="menu" />
          </button>
          <div className="topbar__title">
            <img src="/vgr-logo.png" alt="" className="topbar__logo" />
            <span>{section && section.group ? t(`navGroup.${section.group}`) : t('header.title')}</span>
          </div>
          <div className="topbar__right">
            <LanguageSwitcher />
            <div className="usercard" title={`${t('header.welcome')} ${displayName}`}>
              <div className="avatar">{initials(displayName)}</div>
              <div className="usercard__text">
                <div className="usercard__name">{displayName}</div>
                <div className="usercard__role">{isAdmin ? t('header.owner') : (user.fullName ? user.username : t('header.tenant'))}</div>
              </div>
            </div>
            <button type="button" className="topbar__logout" onClick={onLogout}>
              <Icon name="logout" size={18} />
              <span>{t('header.logout')}</span>
            </button>
          </div>
        </header>

        <main className="app-content">{children}</main>
        <footer className="app-footer">{footer}</footer>
      </div>
    </div>
  );
}
